import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  Optional,
  Output,
  inject,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { NgbActiveModal, NgbPopoverModule } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { isEmpty } from 'lodash';
import { Observable, map, switchMap, filter, of, combineLatest } from 'rxjs';

import { AuraAuthService } from '@digital-platform/aura-authentication/services';
import { User } from '@digital-platform/rs-auth/domain';
import { IdentityService } from '@digital-platform/rs-auth/services';
import { UserMetadata } from '@digital-platform/rs-classic/domain';
import {
  ButtonComponent,
  RichTextLinkComponent,
} from '@digital-platform/rs-cms/feature-ptypes';
import {
  CmsServiceAlertsService,
  ServiceAlertsEntity,
} from '@digital-platform/rs-cms/services';
import {
  NotificationEntity,
  NotificationItem,
  NotificationsService,
} from '@digital-platform/rs-notifications/services';
import { IconComponent } from '@digital-platform/rs-shared/ui-brand';
import {
  AccountMenuItem,
  accountMenuItems,
} from '../account-dropdown-button/account-dropdown-button.constants';

@UntilDestroy()
@Component({
  selector: 'digital-platform-account-dropdown-box',
  standalone: true,
  imports: [
    CommonModule,
    NgbPopoverModule,
    RouterModule,
    IconComponent,
    RichTextLinkComponent,
    ButtonComponent,
  ],
  templateUrl: './account-dropdown-box.component.html',
  styleUrls: ['./account-dropdown-box.component.scss'],
})
export class AccountDropdownBoxComponent {
  @Input() public styles: { [key: string]: string } = {};
  @Output() public linkClick = new EventEmitter<null>();

  public identityService = inject(IdentityService);
  public auraAuthService = inject(AuraAuthService);
  public notificationsService = inject(NotificationsService);
  public cmsServiceAlertsService = inject(CmsServiceAlertsService);

  public isMobile = false;

  public filteredLoginOptions: AccountMenuItem[] = [];

  public userMetaData$: Observable<UserMetadata | null> =
    this.identityService.state$.pipe(
      map(
        (user: User): UserMetadata | null =>
          ({ ...user.profile?.user_metadata } ?? null)
      )
    );

  constructor(@Optional() public activeModal: NgbActiveModal) {
    this.identityService.state$.pipe(untilDestroyed(this)).subscribe({
      next: (user: User | undefined): void => {
        const state: string = user?.state ?? '';

        // Impersonating users are not allowed to see the account menu items
        const isCsr: boolean = user?.profile?.app_metadata?.user_role === 'CSR';

        if (!isCsr) {
          this.filteredLoginOptions = accountMenuItems.filter(
            (option: AccountMenuItem): boolean =>
              option.roles.includes(state) &&
              user?.profile?.app_metadata?.cdh_contact_profile_id !== ''
          );
        }
      },
    });

    // Combine notifications and service alerts observables into one observable so that the notifications bell icon in the menu can be toggled by either one
    const mergedNotificationsAndServiceAlerts$: Observable<
      NotificationItem[] | ServiceAlertsEntity[]
    > = combineLatest([
      this.notificationsService.state$,
      this.cmsServiceAlertsService.state$,
    ]).pipe(
      switchMap((combinedDataTuple) => {
        for (const notificationOrServiceAlert of combinedDataTuple) {
          if (isEmpty(notificationOrServiceAlert)) {
            // If the data is empty in this observable, skip to the next observable
            continue;
          } else {
            // If there is data in either observable, return the data
            return of(notificationOrServiceAlert);
          }
        }

        // If there is no data in either observable, return an empty array
        return of([]);
      }),
      untilDestroyed(this)
    );

    this.identityService.state$
      .pipe(
        filter((data) => !isEmpty(data)), // Filter out empty values so we only react when the user is logged in
        switchMap(() => mergedNotificationsAndServiceAlerts$), // Switch to the second observable after the first observable emits a non-empty value (the user is logged in)
        untilDestroyed(this)
      )
      .subscribe({
        next: (notificationOrServiceAlert): void => {
          this.toggleBellIcon(notificationOrServiceAlert);
        },
      });
  }

  @HostListener('window:resize', ['$event'])
  public onResize(event: Event): void {
    if ((event.target as Window).innerWidth > 1200) {
      this.activeModal?.dismiss();
    }
  }

  public logout(): void {
    this.auraAuthService.logout({ dispatchEvent: true });

    this.activeModal?.dismiss();
    this.linkClick.emit();
  }

  public onLinkClick(): void {
    this.activeModal?.dismiss();
    this.linkClick.emit();
  }

  public toggleBellIcon(
    notificationOrServiceAlert: NotificationEntity[] | ServiceAlertsEntity[]
  ): void {
    const match = this.filteredLoginOptions.find(
      (option) => option.title === 'Notifications'
    );

    if (match) {
      match.icon =
        notificationOrServiceAlert.length > 0 ? 'bell-dot-solid' : 'bell-solid';
    }
  }
}
